export type GridNeighbors = { left: string | null; right: string | null; up: string | null; down: string | null };

// Determines a grid item's visual up/down/left/right neighbors from its ACTUAL rendered position
// (getBoundingClientRect), not from array-index math -- correct regardless of how many columns the CSS
// layout actually wraps to (flex-wrap fitting however many swatches fit the container width, a responsive
// breakpoint, a future redesign...), since it never assumes a fixed column count the way an
// `index ± columnCount` formula would have to.
//
// Rows are grouped by top-edge Y (a half-item-height tolerance absorbs sub-pixel layout rounding, not
// real row differences). Within a row, left/right are simply the adjacent item by X -- but up/down can't
// use "same index within the row" the same way: this grid centers a short last row
// (`justify-content:center`), so that row's item 0 does NOT sit at the same X as a full row's item 0.
// up/down instead cross into the neighboring row and pick whichever item's X-center is closest to this
// item's own X-center, which is what "looks like it's above/below me" actually means to someone looking
// at the screen, independent of row length.
export function getGridNeighbors(
  selfId: string, items: { id: string; el: HTMLElement }[],
): GridNeighbors {
  const none: GridNeighbors = { left: null, right: null, up: null, down: null };
  const self = items.find((i) => i.id === selfId);
  if (!self) return none;

  const rects = items.map((i) => ({ id: i.id, rect: i.el.getBoundingClientRect() }));
  const selfRect = self.el.getBoundingClientRect();
  const rowTolerance = selfRect.height / 2;

  const row = rects
    .filter((r) => Math.abs(r.rect.top - selfRect.top) < rowTolerance)
    .sort((a, b) => a.rect.left - b.rect.left);
  const selfIdxInRow = row.findIndex((r) => r.id === selfId);
  const left = selfIdxInRow > 0 ? row[selfIdxInRow - 1].id : null;
  const right = selfIdxInRow >= 0 && selfIdxInRow < row.length - 1 ? row[selfIdxInRow + 1].id : null;

  const rowTops = [...new Set(rects.map((r) => Math.round(r.rect.top)))].sort((a, b) => a - b);
  const selfRowIdx = rowTops.indexOf(Math.round(selfRect.top));
  const selfCenterX = selfRect.left + selfRect.width / 2;
  const closestInRow = (top: number): string | null => {
    let best: { id: string; dist: number } | null = null;
    for (const r of rects) {
      if (Math.round(r.rect.top) !== top) continue;
      const dist = Math.abs(r.rect.left + r.rect.width / 2 - selfCenterX);
      if (!best || dist < best.dist) best = { id: r.id, dist };
    }
    return best?.id ?? null;
  };
  const up = selfRowIdx > 0 ? closestInRow(rowTops[selfRowIdx - 1]) : null;
  const down = selfRowIdx >= 0 && selfRowIdx < rowTops.length - 1 ? closestInRow(rowTops[selfRowIdx + 1]) : null;

  return { left, right, up, down };
}
